import { AxiosInstance } from 'axios';

/**
 * What a function app has been doing, from its own telemetry.
 *
 * The counterpart to the heartbeat in health.ts. That one comes out of the storage account
 * and always works; this needs an Application Insights key per environment and is where the
 * "why" lives - a host that failed to start, a function that was never invoked, the one
 * exception that accounts for eighty failures. When the key is missing this is the source
 * that goes quiet, and the panel says which one rather than showing nothing.
 */

export interface WorkerInvocation {
    functionName: string;
    total?: number | null;
    failed?: number | null;
    firstAt?: string | null;
    lastAt?: string | null;
}

export interface WorkerHostEvent {
    at?: string | null;
    /** Verbose, Information, Warning, Error or Critical. */
    severity: string;
    /** The runtime's own words, untranslated - those are what you search for. */
    message: string;
}

export interface WorkerError {
    type?: string | null;
    message?: string | null;
    functionName?: string | null;
    /** How often this same failure happened; one invoice or the whole environment. */
    occurrences?: number | null;
    lastAt?: string | null;
}

export interface WorkerActivity {
    appKey: string;
    displayName?: string | null;
    hours: number;
    /** Whether this environment has Application Insights set up at all. */
    configured: boolean;
    /** The deployed app name as its telemetry reports it. */
    roleName?: string | null;
    summary?: string | null;
    invocations: WorkerInvocation[];
    hostEvents: WorkerHostEvent[];
    errors: WorkerError[];
    error?: string | null;
}

/** What one app did over the given window. */
export const getWorkerActivity = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    appKey: string,
    hours?: number
): Promise<WorkerActivity> => {
    const query = hours ? `?hours=${hours}` : '';

    const response = await axios.get(
        `/Health/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/workers/${encodeURIComponent(appKey)}/activity${query}`
    );

    // The axios instance is created with transformResponse: [], so response.data is a
    // string and has to be parsed here.
    return typeof response.data === 'string'
        ? (JSON.parse(response.data) as WorkerActivity)
        : (response.data as WorkerActivity);
};

/** Whether a severity is one worth colouring. */
export const isBadSeverity = (severity: string): boolean =>
    severity === 'Error' || severity === 'Critical';
